import { FLAG_TYPE_COACH } from "@components/Settings/UserDefinedFlags/constants";

// V2 flag helpers. The v2 month-view cell keys `flags` by flag TYPE (verified against the
// backend StatusTrackerBase._build_flags_by_player_id), or gives `[]` when there are none:
//
//   flags = {
//     gps_overworked:  { version: 2 },
//     gps_underworked: { version: 2 },
//     forceplate:      { version: 2, metrics: ["CMJ Height", ...] },
//     testing:         { version: 2, metrics: [...] },
//     user_defined:    [ { user_defined_flag_id, operand_values:{label:val}, name, color, ... }, ... ],
//   }  |  []
//
// These map that type-keyed shape into the FlagsByType structure FlagTooltip renders
// (Forceplate/Testing → "(metrics)", User Defined → coloured flags, GPS over/under → plain).

const GPS_LABELS = { gps_overworked: "Overworked", gps_underworked: "Underworked" };

// A user_defined entry a coach tagged by hand. The month view marks one by the flag's `type`
// (enriched from UserDefinedFlags); the tag response carries `source` as well. Accept either,
// so a cell patched after a save and a cell loaded after a refresh read the same.
export const COACH_SOURCE = "coach";
export const isCoachEntry = (f) => f?.type === FLAG_TYPE_COACH || f?.source === COACH_SOURCE;

const coachFlagsOf = (flags) =>
    !isEmptyFlags(flags) && Array.isArray(flags.user_defined) ? flags.user_defined.filter(isCoachEntry) : [];

const isEmptyFlags = (flags) => !flags || Array.isArray(flags) || Object.keys(flags).length === 0;

// Badge count — null when the day has no player data.
// Per product: every non-UDF category counts as ONE flag regardless of internal detail —
// forceplate/testing show 1 even with many flagged metrics, and GPS over/under collapse to a
// single "GPS". Only user-defined flags are counted individually (one per distinct triggered flag).
export const countFlags = (cellInfo) => {
    const flags = cellInfo?.flags;
    // A coach flag is tagged by hand, so it shows on a day with no player data too.
    if (!cellInfo?.player_data_exists_for_curr_day && coachFlagsOf(flags).length === 0) return null;
    if (isEmptyFlags(flags)) return null;

    const categories = new Set();
    let udfCount = 0;
    for (const [type, meta] of Object.entries(flags)) {
        if (type === "user_defined") {
            udfCount += Array.isArray(meta) ? meta.length : 0;
        } else if (type === "gps_overworked" || type === "gps_underworked") {
            categories.add("gps"); // GPS over/under → one category
        } else {
            categories.add(type); // forceplate / testing / any other → 1 each
        }
    }
    const count = categories.size + udfCount;
    return count > 0 ? count : null;
};

// { flag_type: metadata } → FlagsByType consumed by FlagTooltip.
export const groupFlagsByType = (flags) => {
    const groups = {};
    if (isEmptyFlags(flags)) return groups;

    for (const [type, meta] of Object.entries(flags)) {
        if (type === "forceplate" || type === "testing") {
            const label = type === "forceplate" ? "Forceplate" : "Testing";
            groups[label] = [[label, meta?.metrics || []]];
        } else if (type === "user_defined") {
            const items = (Array.isArray(meta) ? meta : []).map((f) => ({
                name: f.name,
                color: f.color,
                user_defined_flag_id: f.user_defined_flag_id,
                operand_values: f.operand_values || null,
                // A coach flag has no operands; its note is what the name hover shows instead.
                notes: f.notes || null,
            }));
            if (items.length) groups["User Defined"] = [["User Defined", items]];
        } else {
            const label = GPS_LABELS[type] || type;
            if (!groups["GPS"]) groups["GPS"] = [];
            groups["GPS"].push(label);
        }
    }
    return groups;
};

// Options for the flag-type filter. GPS / Forceplate / Testing are one entry each; every
// active user-defined flag config is its own. Values match what selectors.cellMatchesFlagFilter
// expects: "gps" | "forceplate" | "testing" | "udf:<id>".
export const buildFlagTypeOptions = (udfFlags) => [
    { value: "gps", label: "GPS" },
    { value: "forceplate", label: "Forceplate" },
    { value: "testing", label: "Testing" },
    ...(udfFlags || [])
        .filter((f) => f.is_active && f.id != null)
        .map((f) => ({ value: `udf:${f.id}`, label: f.name })),
];
